import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, connect } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Chip,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HandymanIcon from "@mui/icons-material/Handyman";
import {
  GlassContainer,
  GlassProposalCard,
  GlassButton,
  GlassStatusChip,
  GlassIconButton,
} from "../../components/glassmorphism/GlassmorphismStyles";
import { HandymanAppState } from "../../configuration/redux/reducers";
import { getOrderDetails } from "../Orders/actions";
import { getOrderProposals, updateOrderProposal } from "../Orders/actions";
import { Link } from "react-router-dom";
import { OrderProposalStatus } from "../../configuration/api/apiSchema";

interface Props {
  getOrderDetails: (orderId: string) => void;
  getOrderProposals: (orderId: string) => void;
  updateOrderProposal: (orderProposalId: string, status: "Confirmed" | "Rejected") => void;
}

const OrderProposalsPage: React.FC<Props> = (props) => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const currentUserId = useSelector((state: HandymanAppState) => state.user.currentUserDetails?.id);
  const role = useSelector((state: HandymanAppState) => state.user?.authData?.role ?? "customer");
  const { orderDetails } = useSelector((state: HandymanAppState) => state.order);
  const orderProposals = useSelector((state: HandymanAppState) => state.order.orderProposals || []);
  const orderProposalsLoading = useSelector((state: HandymanAppState) => state.order.orderProposalsLoading);
  const orderProposalsError = useSelector((state: HandymanAppState) => state.order.orderProposalsError);

  useEffect(() => {
    if (orderId) {
      props.getOrderDetails(orderId);
      props.getOrderProposals(orderId);
    }
  }, [props, orderId]);

  const handleApproveProposal = async (proposalId: string) => {
    await props.updateOrderProposal(proposalId, "Confirmed");
    if (orderId) {
      // Refresh both proposals and order details to show updated price
      props.getOrderProposals(orderId);
      props.getOrderDetails(orderId);
    }
  };

  const handleRejectProposal = async (proposalId: string) => {
    await props.updateOrderProposal(proposalId, "Rejected");
    if (orderId) {
      props.getOrderProposals(orderId);
    }
  };

  return (
    <GlassContainer>
      <Box
        width={{ xs: "90%", sm: "80%", md: "75%" }}
        mx="auto"
        mt={12}
        pb={4}
      >
        <Box mb={4}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back to Order
        </Button>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" gutterBottom>
            Order Proposals
          </Typography>
        </Box>
        {orderDetails && (
          <Typography variant="subtitle1" color="text.secondary">
            Order: {orderDetails.name || "Order #" + orderId?.substring(0, 8)}
          </Typography>
        )}
      </Box>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        {orderProposalsLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : orderProposalsError ? (
          <Box p={2}>
            <Typography color="error">
              Error loading proposals: {orderProposalsError}
            </Typography>
          </Box>
        ) : orderProposals.length === 0 ? (
          <Typography color="text.secondary" p={2}>
            No proposals for this order yet
          </Typography>
        ) : (
          <Box>
            {orderProposals.map((proposal) => {
              const isSentByCurrentUser =
                (proposal.isInitiatedByCustomer && currentUserId === proposal.customerId) ||
                (!proposal.isInitiatedByCustomer && currentUserId === proposal.handymanId);

              // Fixed logic for customer invitation management:
              // - Customer-initiated (invitation): Customer can manage/cancel their invitations
              // - Handyman-initiated (application): Customer can accept/reject applications
              const canAct = proposal.isInitiatedByCustomer
                ? currentUserId === proposal.customerId    // Customer manages their invitations
                : currentUserId === proposal.customerId;   // Customer acts on handyman applications


              return (
                <GlassProposalCard
                  key={proposal.id}
                  className={proposal.status?.toLowerCase()}
                  sx={{ p: 3, mb: 2 }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <HandymanIcon 
                        sx={{ 
                          color: '#005CAD',
                          fontSize: '20px'
                        }} 
                      />
                      <Typography 
                        variant="subtitle1"
                        sx={{
                          fontFamily: '"Montserrat", sans-serif',
                          fontWeight: 600,
                          color: 'rgba(26, 26, 26, 0.9)'
                        }}
                      >
                        <Link 
                          to={`/browse-handyman/${proposal?.handyman?.id}`} 
                          style={{ 
                            textDecoration: 'none', 
                            color: 'inherit',
                            transition: 'color 0.3s ease'
                          }}
                        >
                          {proposal?.handyman
                            ? `${proposal.handyman.firstName} ${proposal.handyman.lastName}`
                            : "N/A"}
                        </Link>
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <GlassStatusChip
                        label={isSentByCurrentUser ? "Sent" : "Received"}
                        size="small"
                        status={isSentByCurrentUser ? "sent" : "received"}
                        sx={{
                          backgroundColor: isSentByCurrentUser ? 'rgba(0, 92, 173, 0.2)' : 'rgba(0, 232, 231, 0.2)',
                          borderColor: isSentByCurrentUser ? 'rgba(0, 92, 173, 0.3)' : 'rgba(0, 232, 231, 0.3)',
                        }}
                      />
                      <GlassStatusChip
                        label={proposal.status}
                        size="small"
                        status={
                          proposal.status === OrderProposalStatus.Confirmed
                            ? "completed"
                            : proposal.status === OrderProposalStatus.Rejected
                            ? "rejected"
                            : proposal.status === OrderProposalStatus.Cancelled
                            ? "cancelled"
                            : "pending"
                        }
                      />
                    </Box>
                  </Box>
                  <Box mt={2.5} sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography 
                      variant="body1" 
                      sx={{
                        color: 'rgba(26, 26, 26, 0.8)',
                        fontFamily: '"Roboto", sans-serif',
                        lineHeight: 1.5,
                        marginBottom: 1.5
                      }}
                    >
                      {proposal.order?.description || "No description provided"}
                    </Typography>
                    <Box>
                      <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                        <Typography 
                          variant="body2" 
                          sx={{
                            color: 'rgba(26, 26, 26, 0.7)',
                            fontFamily: '"Roboto", sans-serif'
                          }}
                        >
                          Order Price: 
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#00E8E7',
                            fontWeight: 700,
                            fontFamily: '"Montserrat", sans-serif'
                          }}
                        >
                          £{proposal.order?.price || 0}
                        </Typography>
                      </Box>
                      
                      {/* Show custom proposed price if it exists and differs from order price */}
                      {proposal.proposedPrice && 
                       proposal.proposedPrice !== proposal.order?.price && (
                        <Box 
                          sx={{ 
                            mt: 1, 
                            p: 1.5, 
                            bgcolor: 'rgba(76, 175, 80, 0.1)', 
                            borderRadius: 2,
                            border: '1px solid rgba(76, 175, 80, 0.3)'
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                            <Typography 
                              variant="body2" 
                              sx={{
                                color: 'rgba(76, 175, 80, 0.9)',
                                fontFamily: '"Roboto", sans-serif',
                                fontWeight: 600
                              }}
                            >
                              Handyman Proposed Price: 
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#4CAF50',
                                fontWeight: 700,
                                fontFamily: '"Montserrat", sans-serif'
                              }}
                            >
                              £{proposal.proposedPrice}
                            </Typography>
                          </Box>
                          <Typography 
                            variant="caption" 
                            sx={{
                              color: 'rgba(76, 175, 80, 0.8)',
                              fontFamily: '"Roboto", sans-serif'
                            }}
                          >
                            Accepting this proposal will update the order price
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  {canAct && proposal.status === OrderProposalStatus.Requested && (
                    <Box mt={3} display="flex" gap={1.5} sx={{ position: 'relative', zIndex: 1 }}>
                      {proposal.isInitiatedByCustomer ? (
                        // Customer-initiated proposal: Customer can cancel their invitation
                        <GlassButton
                          variant="outlined"
                          onClick={() => handleRejectProposal(proposal.id!)}
                          size="small"
                          sx={{
                            borderColor: 'rgba(244, 67, 54, 0.3)',
                            color: 'rgba(244, 67, 54, 0.9)',
                            '&:hover': {
                              borderColor: 'rgba(244, 67, 54, 0.5)',
                              backgroundColor: 'rgba(244, 67, 54, 0.1)',
                              transform: 'translateY(-1px)',
                            }
                          }}
                        >
                          Cancel Invitation
                        </GlassButton>
                      ) : (
                        // Handyman-initiated proposal: Customer can accept/reject
                        <>
                          <GlassButton
                            variant="contained"
                            onClick={() => handleApproveProposal(proposal.id!)}
                            size="small"
                            sx={{
                              background: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)',
                              color: 'white',
                              fontWeight: 600,
                              '&:hover': {
                                background: 'linear-gradient(135deg, #388E3C 0%, #66BB6A 100%)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 6px 12px rgba(76, 175, 80, 0.3)',
                              }
                            }}
                          >
                            {proposal.proposedPrice && proposal.proposedPrice !== proposal.order?.price
                              ? `Accept Custom Price (£${proposal.proposedPrice})`
                              : 'Accept Proposal'
                            }
                          </GlassButton>
                          <GlassButton
                            variant="outlined"
                            onClick={() => handleRejectProposal(proposal.id!)}
                            size="small"
                            sx={{
                              borderColor: 'rgba(244, 67, 54, 0.3)',
                              color: 'rgba(244, 67, 54, 0.9)',
                              '&:hover': {
                                borderColor: 'rgba(244, 67, 54, 0.5)',
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                transform: 'translateY(-1px)',
                              }
                            }}
                          >
                            Reject
                          </GlassButton>
                        </>
                      )}
                    </Box>
                  )}
                </GlassProposalCard>
              );
            })}
          </Box>
        )}
      </Paper>
      </Box>
    </GlassContainer>
  );
};

const mapDispatchToProps = {
  getOrderDetails,
  getOrderProposals,
  updateOrderProposal,
};

export default connect(null, mapDispatchToProps)(OrderProposalsPage); 